import React from 'react'
import styled from '@emotion/styled'
import { v4 as uuidv4 } from 'uuid'
import { UserPreference, CounterpartyGroup, Severity, ValidationType } from 'api'
import { DialogRow, DialogField, TextInputField } from 'view/components/inputs'
import { UserCounterpartyMultiComboboxField } from '../entity'
import { TextButton, TextButtonType, ButtonStrip, IconButton } from 'view/components/buttons'
import { Listbox, OptionTemplateProps } from 'view/components/options'
import { notEmpty } from 'utils'
import * as constants from 'view/styles/constants'
import { iconSet } from 'view/components/icons'
import { Locale, Translation } from 'locale'

export interface CounterpartyGroupsPanelProps {
  userPreference: UserPreference
  readOnly: boolean
  onUpdate: (counterpartyGroups: CounterpartyGroup[]) => unknown
}

interface CounterpartyGroupsPanelState {
  selectedGroupId: string | null
  selectedGroupDescription: string | null
  selectedCounterpartyIds: string[]
}

const CounterpartyGroupsListbox = styled(Listbox)`
  li {
    height: 40px;
  }

  li:not(:last-child) {
    margin-bottom: 9px;
  }
`

const SelectedOption = styled.div<OptionTemplateProps<string, string>>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 15px 0 10px;
  border-radius: 2px;
  background-color: ${constants.CORE_PRIMARY_1};

  input,
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 0 0 60%;
  }
`

const StyledIconButton = styled(IconButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${constants.CORE_PRIMARY};
  &:first-of-type {
    border-top-left-radius: ${constants.panelBorderRadius};
    border-bottom-left-radius: ${constants.panelBorderRadius};
  }
  &:last-child {
    border-top-right-radius: ${constants.panelBorderRadius};
    border-bottom-right-radius: ${constants.panelBorderRadius};
  }
  &:not(:last-child) {
    margin-right: 1px;
  }
  &:hover {
    color: ${constants.ACCENT_PRIMARY};
  }
`

export class CounterpartyGroupsPanel extends React.Component<CounterpartyGroupsPanelProps, CounterpartyGroupsPanelState> {
  constructor(props: CounterpartyGroupsPanelProps) {
    super(props)
    this.state = {
      selectedGroupId: null,
      selectedGroupDescription: null,
      selectedCounterpartyIds: []
    }
  }

  render() {
    const { userPreference, readOnly } = this.props
    const { selectedGroupId, selectedGroupDescription, selectedCounterpartyIds } = this.state

    const validationIssues =
      notEmpty(selectedGroupId) && !notEmpty(selectedGroupDescription)
        ? [
            {
              errorMessage: Locale.translate(Translation.SC_MISSING_MANDATORY),
              fieldName: '',
              severity: Severity.ERROR,
              type: ValidationType.MISSING_MANDATORY
            }
          ]
        : []

    return (
      <>
        <CounterpartyGroupsListbox
          optionKeys={userPreference.counterpartyGroups.map(item => item.id)}
          getOption={(itemKey: string) => userPreference.counterpartyGroups.find(item => item.id === itemKey) || null}
          getOptionText={(item: CounterpartyGroup) => item.description}
          optionTemplate={this.renderSelectedOption}
          readOnly={readOnly}
          noItemsText={Locale.translate(Translation.SC_NO_COUNTERPARTY_GROUPS)}
        />
        <TextInputField
          label={Locale.translate(Translation.SC_GROUP_NAME)}
          validationIssues={validationIssues}
          readOnly={readOnly}
          value={selectedGroupDescription}
          onUpdate={this.handleUpdateGroupDescription}
        />
        <UserCounterpartyMultiComboboxField
          userId={userPreference.id}
          label={Locale.translate(Translation.SC_MEMBERS)}
          canClear={true}
          readOnly={readOnly}
          value={selectedCounterpartyIds}
          onUpdate={this.handleUpdateCounterpartyIds}
        />
        <DialogRow horizontalLayout={constants.HorizontalLayout.Right}>
          <DialogField flex={0}>
            <TextButton
              type={TextButtonType.Tertiary}
              disabled={
                readOnly || (!notEmpty(selectedGroupId) && !notEmpty(selectedGroupDescription) && selectedCounterpartyIds.length === 0)
              }
              onClick={this.handleClearSelectedGroup}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              type={TextButtonType.Tertiary}
              disabled={readOnly || selectedCounterpartyIds.length === 0 || !notEmpty(selectedGroupDescription)}
              onClick={this.handleSave}>
              {selectedGroupId ? Locale.translate(Translation.TC_BUTTON_SAVE_GROUP) : Locale.translate(Translation.TC_BUTTON_CREATE_GROUP)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </>
    )
  }

  private renderSelectedOption = (optionProps: OptionTemplateProps<string, string>) => {
    return (
      <SelectedOption {...optionProps}>
        <span>{optionProps.optionText}</span>
        <ButtonStrip>
          <StyledIconButton
            disabled={this.props.readOnly}
            onClick={() => this.handleEdit(optionProps.optionKey)}
            dataTestId="CounterpartyGroupsPanel-EditGroupButton">
            {iconSet.editIcon}
          </StyledIconButton>
          <StyledIconButton
            disabled={this.props.readOnly}
            onClick={() => this.handleRemove(optionProps.optionKey)}
            dataTestId="CounterpartyGroupsPanel-RemoveGroupButton">
            {iconSet.crossIcon}
          </StyledIconButton>
        </ButtonStrip>
      </SelectedOption>
    )
  }

  private handleUpdateGroupDescription = (selectedGroupDescription: string) => {
    this.setState({ selectedGroupDescription })
  }

  private handleUpdateCounterpartyIds = (selectedCounterpartyIds: string[]) => {
    this.setState({ selectedCounterpartyIds })
  }

  private handleEdit = (selectedGroupId: string) => {
    const selectedCounterpartyGroup = this.props.userPreference.counterpartyGroups.find(group => group.id === selectedGroupId)

    this.setState({
      selectedGroupId,
      selectedGroupDescription: selectedCounterpartyGroup ? selectedCounterpartyGroup.description : null,
      selectedCounterpartyIds: selectedCounterpartyGroup ? selectedCounterpartyGroup.counterpartyIds : []
    })
  }

  private handleClearSelectedGroup = () => {
    this.setState({
      selectedGroupId: null,
      selectedGroupDescription: null,
      selectedCounterpartyIds: []
    })
  }

  private handleRemove = (id: string) => {
    const { userPreference, onUpdate } = this.props
    const newCounterpartyGroups = userPreference.counterpartyGroups.filter(group => group.id !== id)

    onUpdate(newCounterpartyGroups)
  }

  private handleSave = () => {
    const {
      userPreference: { counterpartyGroups },
      onUpdate
    } = this.props
    const { selectedGroupId, selectedGroupDescription, selectedCounterpartyIds } = this.state
    const id = selectedGroupId || uuidv4()
    let newCounterpartyGroups: CounterpartyGroup[] = []

    if (!selectedGroupId) {
      newCounterpartyGroups = [
        ...counterpartyGroups,
        {
          id,
          description: selectedGroupDescription || id,
          counterpartyIds: selectedCounterpartyIds
        }
      ]
    } else {
      const selectedCounterpartyGroupIdx = counterpartyGroups.findIndex(group => group.id === selectedGroupId)
      const selectedCounterpartyGroup = counterpartyGroups.find(group => group.id === selectedGroupId)
      if (selectedCounterpartyGroupIdx !== -1) {
        newCounterpartyGroups = [
          ...counterpartyGroups.slice(0, selectedCounterpartyGroupIdx),
          {
            ...selectedCounterpartyGroup!,
            description: selectedGroupDescription || selectedCounterpartyGroup!.description,
            counterpartyIds: selectedCounterpartyIds
          },
          ...counterpartyGroups.slice(selectedCounterpartyGroupIdx + 1)
        ]
      }
    }

    onUpdate(newCounterpartyGroups)
    this.handleClearSelectedGroup()
  }
}
